import type { Dict } from "../core.js";
import type { bankHubLab as vi } from "../vi/bankHubLab.js";

export const bankHubLab: Dict<typeof vi> = {
  title: "SePay Bank Hub lab",
  subtitle: "Set « {name} » · {env} · {product}",
  hostEnv: "environment by host: {env}",
  intro:
    "Calls the REAL Bank Hub API with this credential set, before it is bound to an app. Red buttons create / change / revoke real things at SePay — click twice; the server requires confirmation and writes an audit entry (without parameters).",
  readOnlyRole: "Your role is read-only — calling SePay needs the integrations write permission (it uses the set's stored keys).",
  notFound: "Credential set not found (or it is not a SePay Bank Hub product).",
  envMismatchTitle: "The set's environment does not match its host — the server blocks every write action",
  infoFailed: "Could not load the lab info: {error}",
  infoLoading: "Loading the lab info…",
  "shared.title": "This Client ID is serving an app",
  "shared.row": "{app} · {env} · set « {set} »",
  "shared.sameSet": "(this set)",
  "shared.grace": "(in grace period)",
  "shared.help":
    "SePay keeps ONE event webhook and ONE IPN keyword filter per Client ID — pointing the webhook at the lab or changing the merchant config would take the app's messages away, so the server blocks both.",
  "channel.ipn.title": "Balance-change IPN",
  "channel.ipn.help":
    "Paste this URL into the SePay portal › Bank Hub › « Cấu hình Webhook » › « Notify URL » (there is no API to set the IPN URL). The « Notify API Key » there must equal the set's « IPN key » (ipnApiKey). One Notify URL per merchant — pointing it at the lab stops IPN for any app using the same Client ID.",
  "channel.events.title": "Link events (BANK_ACCOUNT_*, LINK_*)",
  "channel.events.help": "Set it with « Update webhook » (Merchant group) — one URL per Client ID; it overwrites the old URL and applies immediately.",
  "channel.auth": "SePay sends the header {header}",
  "channel.keyHint": "key the lab checks: {hint}",
  "channel.keyMissing": "The set has no key for this channel — every message sent here will be rejected (401).",
  "channel.urlFallback": "The hub server has no HQ_PUBLIC_API_URL — the URL is built from the dashboard address; SePay only calls public https:// URLs.",
  "channel.openWebhookForm": "Open « Update webhook »",
  flows: "Scenarios",
  "flow.suite": "Run every read API",
  "flow.link": "Account link session (Hosted Link)",
  "help.suite":
    "Runs EVERY read-only API in a row, small page sizes, at least 350 ms apart — changes nothing at SePay. Detail steps use the first company / account / transaction of the list steps.",
  "help.link":
    "Check the event webhook ⇒ pick or create a company ⇒ create a link token ⇒ Hosted Link in a frame ⇒ wait for a BANK_ACCOUNT_* event at the lab ⇒ read the account detail. On Live, whoever completes the session links / unlinks a REAL bank account.",
  "section.read": "Read (changes nothing)",
  "section.company": "Company",
  "section.link": "Linking (link token)",
  "section.merchant": "Merchant (whole Client ID)",
  "action.token": "Get token",
  "action.companies": "List companies",
  "action.company": "Company detail",
  "action.companyCounter": "Company transaction counter",
  "action.bankAccounts": "List bank accounts",
  "action.bankAccount": "Bank account detail",
  "action.transactions": "List transactions",
  "action.transaction": "Transaction detail",
  "action.merchantConfig": "Merchant config",
  "action.webhook": "Current event webhook",
  "action.linkTokenGet": "Link token info",
  "action.companyCreate": "Create company",
  "action.companyEdit": "Edit company",
  "action.linkTokenCreate": "Create link token",
  "action.linkTokenRevoke": "Revoke link token",
  "action.merchantConfigUpdate": "Edit merchant config",
  "action.webhookUpdate": "Update webhook",
  "scope.session": "Affects ONE link session — on Live, whoever completes it links / unlinks a real bank account.",
  "scope.company":
    "Affects ONE company — status / transaction limit can make SePay STOP sending IPN for every account of the company. SePay has no API to delete a company.",
  "scope.merchant": "Affects the WHOLE merchant (every company using this Client ID): the event webhook and the IPN keyword filter.",
  "hint.token": "The server only returns ttl — the access token never leaves the server.",
  "hint.companyCounter": "Leave the date empty for all dates.",
  "hint.bankAccount": "Citizen ID and phone number are masked before they are returned.",
  "hint.transactions": "Dates: yyyy-mm-dd (the only form SePay documents). webhookSuccess = 0 ⇒ transactions whose IPN delivery failed — a sign the IPN points to the wrong place.",
  "hint.linkTokenGet": "Use the link_token string (UUID) returned by « Create link token » — not the xid.",
  "hint.linkTokenRevoke": "Cannot be undone — it disconnects whoever is in the session. Use the link token's xid, not the link_token string.",
  "hint.companyEdit":
    "SePay's docs only list status Active / Pending. Suspended: measured on sandbox 2026-10-16 — SePay accepts it and « Company detail » then returns Suspended; whether IPN actually stops is NOT measured yet. Other values are unmeasured. Transaction limit: Unlimited or a number; 0 ⇒ SePay stops sending IPN for the company.",
  "hint.merchantConfigUpdate":
    "One keyword per line (or comma). Empty list ⇒ SePay sends IPN for every transaction; with keywords ⇒ only transactions whose content contains one — for EVERY company.",
  "field.page": "Page",
  "field.perPage": "Rows per page",
  "field.q": "Search (q)",
  "field.status": "Status",
  "field.companyXid": "Company xid",
  "field.date": "Date (yyyy-mm-dd)",
  "field.bankId": "Bank code (bank_id)",
  "field.bankAccountXid": "Bank account xid",
  "field.transferType": "Direction (transfer_type)",
  "field.webhookSuccess": "IPN delivered (webhook_success)",
  "field.transactionDate": "Transaction date (yyyy-mm-dd)",
  "field.startTransactionDate": "From (yyyy-mm-dd)",
  "field.endTransactionDate": "To (yyyy-mm-dd)",
  "field.transactionId": "Transaction ID (transaction_id)",
  "field.fullName": "Full name (full_name, unique within the merchant)",
  "field.shortName": "Short name (short_name)",
  "field.transactionAmount": "Transaction limit (Unlimited or a number)",
  "field.paymentCode": "Payment code (payment_code)",
  "field.paymentCodePrefix": "Payment code prefix (2–5 letters)",
  "field.paymentCodeSuffixFrom": "Suffix from (characters)",
  "field.paymentCodeSuffixTo": "Suffix to (characters)",
  "field.paymentCodeSuffixCharacterType": "Suffix character type (e.g. NumberOnly)",
  "field.purpose": "Purpose",
  "field.completionRedirectUri": "Return URL after completion (https)",
  "field.isMobileApp": "Mobile app version",
  "field.language": "Page language",
  "field.linkToken": "link_token (UUID)",
  "field.linkTokenXid": "Link token xid",
  "field.transactionWhitelistRemarkKeywords": "Transaction content keywords",
  "form.required": "« {field} » is required.",
  "form.notNumber": "« {field} » must be a whole number.",
  "account.inactive": "inactive",
  "result.title": "Result",
  "result.empty": "No API called yet.",
  "result.noHttp": "did not reach SePay",
  "result.base": "{url} · {env}",
  "hosted.title": "SePay Bank Hub Hosted Link",
  "hosted.help":
    "Pick the bank, sign in and confirm the OTP on SePay's page. Browser messages are only signals — the source of truth is the BANK_ACCOUNT_* event at the lab.",
  "hosted.expired": "The Hosted Link session has expired — create a new link token.",
  "hosted.origin": "origin {origin}",
  "hosted.expiresAt": "expires {at}",
  "hosted.signal": "Hosted Link says: {name}",
  "hosted.newTab": "Open in a new tab",
  "hosted.close": "Close",
  "hosted.ready": "Hosted Link created — origin {origin} is in the set's list.",
  "hosted.notAllowed":
    "Origin {origin} is NOT in the set's « Hosted Link origins » ({allowed}) — not embedded. Check the URL before opening a new tab; if it really is SePay's, add the origin to the set.",
  "hosted.notAllowedShort": "origin {origin} is not in the set's list — not embedded",
  "hosted.open": "Open Hosted Link",
  "events.title": "Messages from SePay (refreshes every 3 seconds)",
  "events.empty": "No messages received yet.",
  "events.clear": "Clear log",
  "col.time": "Received",
  "col.kind": "Kind",
  "col.auth": "Key",
  "col.amount": "Amount",
  "col.account": "Account",
  "col.content": "Content",
  verifiedYes: "Key OK",
  verifiedNo: "Wrong ({reason})",
  "wh.noTargets": "The server returned no webhook targets.",
  "wh.target": "Target",
  "wh.targetLab": "This set's lab",
  "wh.targetApp": "App {app} (production, {env})",
  "wh.urlMissing": "(no URL — the hub has no HQ_PUBLIC_API_URL or the app has no URL declared)",
  "wh.active": "Active (untick = turn the webhook off)",
  "wh.events": "Events",
  "wh.eventsAll": "All events (*)",
  "wh.eventsList": "Pick events",
  "wh.reactivatedNote": "BANK_ACCOUNT_REACTIVATED is not in SePay's list of selectable events (wrong name ⇒ 400) — choose « All events » to receive it.",
  "wh.needEvents": "Pick at least one event.",
  "wh.receiverOther": "The receiver checks X-Secret-Key with a DIFFERENT set than the one under test — it will reject these events.",
  "wh.serverBuilds":
    "The URL and secret_key are built by the SERVER (X-Secret-Key = the set's event key). The result includes the old URL (previous) so it can be restored.",
  "wh.readCurrent": "Read current webhook",
  "ptr.none": "No event webhook set.",
  "ptr.lab": "Points at this set's lab (events: {events}).",
  "ptr.app": "Points at app {app} — events will NOT reach the lab.",
  "ptr.other": "Points somewhere else: {url}",
  "ptr.inactive": "webhook is OFF",
  "suite.ttl": "token lives {s} seconds",
  "suite.rows": "{n} rows",
  "suite.afterTokenFailed": "Skipped — getting a token failed.",
  "suite.needId": "Skipped — no {what} yet.",
  "suite.summary": "Passed {ok} · failed {failed} · skipped {skipped}",
  "need.companyXid": "company (the list was empty)",
  "need.bankAccountXid": "bank account (the list was empty)",
  "need.transactionId": "transaction (the list was empty)",
  "need.linkToken": "link token (create one in « Account link session » or « Create link token » first)",
  "link.step.webhook": "Event webhook points at the lab",
  "link.step.company": "Company",
  "link.step.session": "Create link token",
  "link.step.hosted": "Hosted Link",
  "link.step.event": "Wait for a BANK_ACCOUNT_* event",
  "link.step.account": "Read account detail",
  "link.noXid": "SePay returned no xid for the new company — see the Result panel.",
  "link.companyCreated": "Company {xid} created.",
  "link.noHostedUrl": "The result has no usable https:// hosted_link_url.",
  "link.expect": "waiting for {events}",
  "link.revoked": "Link token revoked — waiting for LINK_TOKEN_REVOKED.",
  "link.restored": "Restored this tab's unfinished session (link token {xid}) — still waiting for that session's events. « Start over » to drop it.",
  "link.restoredDone": "Restored this tab's previous session (link token {xid}) — no longer waiting for events; the step results are above. « Start over » for a new session.",
  "link.sandboxHint":
    "Sandbox: use the demo accounts + fixed OTPs on SePay's sandbox info page. Reactivation can only be tried with OCB / KienLongBank (unlink first, then reactivate). Sandbox cannot simulate transactions / IPN.",
  "link.sandboxDocs": "SePay sandbox info",
  "link.eventsUnusable": "The lab's event URL cannot be used: {reason}",
  "link.webhookWarn":
    "Events do not point at the lab — the « Wait for event » step will not finish by itself. Point it with « Update webhook » to the lab (only allowed when the Client ID is not serving an app).",
  "link.allowEvents.none": "allow_events is empty — SePay sends NO events; set « All events » again with « Update webhook ».",
  "link.allowEvents.needAll":
    "this session waits for {events} — that name cannot be picked individually, it only arrives when the webhook is set to « All events » (allow_events [\"*\"]).",
  "link.allowEvents.missing": "allow_events does not include {events} — SePay will not send it; set « All events » or add that name.",
  "link.company": "Company",
  "link.pickCompany": "— pick a company —",
  "link.noCompanies": "The merchant has no companies yet.",
  "link.loadCompanies": "Load companies",
  "link.newCompany": "Or create a new company (full name)",
  "link.companyPermanent": "SePay has no API to delete a company — a company created here stays forever.",
  "link.purpose": "Purpose",
  "link.account": "Bank account",
  "link.pickAccount": "— pick an account —",
  "link.noAccounts": "The company has no accounts yet.",
  "link.loadAccounts": "Load the company's accounts",
  "link.reactivateHint": "Reactivation only works for an account DEACTIVATED by an unlink (OCB, MSB, KienLongBank, TPBank); any other account ⇒ SePay returns 400.",
  "link.checkWebhook": "Check webhook",
  "link.readSession": "Read session (link-token/get)",
  "link.reopen": "Reopen Hosted Link",
  "link.start": "Create link token & open Hosted Link",
  "link.needCompany": "Pick or create a company first.",
  "link.needAccount": "Pick a bank account (required to unlink / reactivate).",
  "purpose.LINK_BANK_ACCOUNT": "Link account",
  "purpose.UNLINK_BANK_ACCOUNT": "Unlink account",
  "purpose.REACTIVATE_BANK_ACCOUNT": "Reactivate account",
};
